import React, { useState } from 'react';
import './TaxBracketAlerts.css';

interface TaxBracketAlertsProps {
  detailedData: any[];
  province?: string;
  currentYear: number;
  inflationRate?: number;
}

interface TaxBracket {
  name: string;
  min: number;
  max: number;
  rate: number;
}

const TaxBracketAlerts: React.FC<TaxBracketAlertsProps> = ({
  detailedData,
  province = 'Ontario',
  currentYear,
  inflationRate = 0.03
}) => {
  const [selectedYear, setSelectedYear] = useState(detailedData[0]?.year || currentYear);

  if (!detailedData || detailedData.length === 0) {
    return <div className="tax-bracket-alerts">No data available</div>;
  }

  // Federal brackets (2024 base)
  const federalBrackets: TaxBracket[] = [
    { name: 'Federal Basic (15%)', min: 0, max: 55867, rate: 0.15 },
    { name: 'Federal 2nd (20.5%)', min: 55867, max: 111733, rate: 0.205 },
    { name: 'Federal 3rd (26%)', min: 111733, max: 173205, rate: 0.26 },
    { name: 'Federal 4th (29%)', min: 173205, max: 246752, rate: 0.29 },
    { name: 'Federal Top (33%)', min: 246752, max: Infinity, rate: 0.33 },
  ];

  // Ontario brackets (2024 base)
  const ontarioBrackets: TaxBracket[] = [
    { name: 'Ontario Basic (5.05%)', min: 0, max: 51446, rate: 0.0505 },
    { name: 'Ontario 2nd (9.15%)', min: 51446, max: 102894, rate: 0.0915 },
    { name: 'Ontario 3rd (11.16%)', min: 102894, max: 150000, rate: 0.1116 },
    { name: 'Ontario 4th (12.16%)', min: 150000, max: 220000, rate: 0.1216 },
    { name: 'Ontario Top (13.16%)', min: 220000, max: Infinity, rate: 0.1316 },
  ];

  // Adjust brackets for inflation
  const adjustBracketsForInflation = (brackets: TaxBracket[], yearsFromNow: number): TaxBracket[] => {
    const inflationFactor = Math.pow(1 + inflationRate, yearsFromNow);
    return brackets.map(b => ({
      ...b,
      min: b.min * inflationFactor,
      max: b.max === Infinity ? Infinity : b.max * inflationFactor
    }));
  };

  // Find which bracket income falls into
  const findBracket = (income: number, brackets: TaxBracket[]): { bracket: TaxBracket; nextBracket: TaxBracket | null } => {
    const bracket = brackets.find(b => income >= b.min && income < b.max) || brackets[brackets.length - 1];
    const currentIndex = brackets.indexOf(bracket);
    const nextBracket = currentIndex < brackets.length - 1 ? brackets[currentIndex + 1] : null;
    return { bracket, nextBracket };
  };

  const yearData = detailedData.find(d => d.year === selectedYear) || detailedData[0];
  const yearsFromNow = selectedYear - currentYear;

  // Adjust brackets for inflation
  const adjustedFederal = adjustBracketsForInflation(federalBrackets, yearsFromNow);
  const adjustedOntario = adjustBracketsForInflation(ontarioBrackets, yearsFromNow);

  // Calculate total taxable income
  const taxableIncome = (yearData.cpp_income || 0) +
                        (yearData.oas_income || 0) +
                        (yearData.pension_income || 0) +
                        (yearData.rrsp_withdrawal || 0) +
                        (yearData.rrif_withdrawal || 0);

  const { bracket: federalBracket, nextBracket: nextFederalBracket } = findBracket(taxableIncome, adjustedFederal);
  const { bracket: ontarioBracket, nextBracket: nextOntarioBracket } = findBracket(taxableIncome, adjustedOntario);

  const federalHeadroom = nextFederalBracket ? nextFederalBracket.min - taxableIncome : 0;
  const ontarioHeadroom = nextOntarioBracket ? nextOntarioBracket.min - taxableIncome : 0;
  const combinedMarginalRate = federalBracket.rate + ontarioBracket.rate;

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(val);

  const formatPercent = (val: number) => `${(val * 100).toFixed(2)}%`;

  // Determine alert level (🟢 green, 🟡 yellow, 🔴 red)
  const getAlertLevel = () => {
    if (!nextFederalBracket && !nextOntarioBracket) return '🟢'; // Top bracket, no worry
    const minHeadroom = Math.min(federalHeadroom, ontarioHeadroom);
    if (minHeadroom > 50000) return '🟢'; // Plenty of room
    if (minHeadroom > 20000) return '🟡'; // Getting close
    return '🔴'; // Very close to next bracket
  };

  const getOpportunityMessage = () => {
    if (!nextFederalBracket && !nextOntarioBracket) {
      return "You're in the top tax bracket. Consider income splitting or tax-loss harvesting strategies.";
    }

    const minHeadroom = Math.min(federalHeadroom, ontarioHeadroom);

    if (minHeadroom > 50000) {
      return `You have ${formatCurrency(minHeadroom)} of room in your current tax bracket. Consider withdrawing more from RRSP/RRIF to fill this space efficiently.`;
    }

    if (minHeadroom > 20000) {
      return `You're approaching the next tax bracket with ${formatCurrency(minHeadroom)} remaining. Plan RRSP withdrawals carefully to avoid jumping brackets.`;
    }

    return `⚠️ You're very close to the next tax bracket (${formatCurrency(minHeadroom)} remaining). Consider maximizing TFSA withdrawals instead of RRSP to avoid higher taxes.`;
  };

  return (
    <div className="tax-bracket-alerts">
      <div className="tax-header">
        <div>
          <h3>Tax Bracket Opportunity Analysis</h3>
          <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
            Find opportunities to optimize withdrawals and minimize taxes
          </p>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="year-selector"
        >
          {detailedData.map(d => (
            <option key={d.year} value={d.year}>
              {d.year} (Age {d.age})
            </option>
          ))}
        </select>
      </div>

      <div className="current-situation">
        <div className="situation-card">
          <div className="card-label">Taxable Income</div>
          <div className="card-value">{formatCurrency(taxableIncome)}</div>
        </div>
        <div className="situation-card">
          <div className="card-label">Combined Marginal Rate</div>
          <div className="card-value">{formatPercent(combinedMarginalRate)}</div>
          <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
            Federal: {formatPercent(federalBracket.rate)} + {province}: {formatPercent(ontarioBracket.rate)}
          </div>
        </div>
        <div className="situation-card">
          <div className="card-label">Total Taxes Paid</div>
          <div className="card-value">{formatCurrency(yearData.taxes || 0)}</div>
        </div>
      </div>

      <div className="bracket-details">
        <div className="bracket-section">
          <h4>Federal Tax Bracket</h4>
          <div className="bracket-info">
            <div className="bracket-current">
              <strong>Current:</strong> {federalBracket.name}
              <div className="bracket-range">
                {formatCurrency(federalBracket.min)} - {federalBracket.max === Infinity ? '∞' : formatCurrency(federalBracket.max)}
              </div>
            </div>
            {nextFederalBracket && (
              <div className="bracket-next">
                <strong>Next Bracket:</strong> {nextFederalBracket.name} <br />
                <span className="headroom">Headroom: {formatCurrency(federalHeadroom)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bracket-section">
          <h4>{province} Tax Bracket</h4>
          <div className="bracket-info">
            <div className="bracket-current">
              <strong>Current:</strong> {ontarioBracket.name}
              <div className="bracket-range">
                {formatCurrency(ontarioBracket.min)} - {ontarioBracket.max === Infinity ? '∞' : formatCurrency(ontarioBracket.max)}
              </div>
            </div>
            {nextOntarioBracket && (
              <div className="bracket-next">
                <strong>Next Bracket:</strong> {nextOntarioBracket.name} <br />
                <span className="headroom">Headroom: {formatCurrency(ontarioHeadroom)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`opportunity-alert ${getAlertLevel()}`}>
        <div className="alert-icon">{getAlertLevel()}</div>
        <div className="alert-message">
          <strong>Opportunity:</strong> {getOpportunityMessage()}
        </div>
      </div>

      <div className="bracket-tips">
        <h4>Tax Optimization Tips</h4>
        <ul>
          <li>💡 <strong>RRSP/RRIF Withdrawals:</strong> Fully taxable at your marginal rate. Use headroom wisely.</li>
          <li>💡 <strong>TFSA Withdrawals:</strong> Tax-free! Perfect when you're near the next bracket.</li>
          <li>💡 <strong>Income Splitting:</strong> If married, consider pension income splitting to reduce taxes.</li>
          <li>💡 <strong>Capital Gains:</strong> Only 50% is taxable, making non-registered accounts more tax-efficient in higher brackets.</li>
        </ul>
      </div>
    </div>
  );
};

export default TaxBracketAlerts;
